import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix de los iconos default de Leaflet con bundlers tipo Webpack/CRA
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl, iconUrl, shadowUrl });

const JUJUY_CENTER = { lat: -24.1858, lng: -65.2995 };

// Recentra el mapa cuando cambian las coordenadas externas (ej. tras búsqueda)
function RecenterMap({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo([position.lat, position.lng], Math.max(map.getZoom(), 14));
  }, [position, map]);
  return null;
}

// Captura clicks en el mapa para mover el marker
function ClickHandler({ onPick, readonly }) {
  useMapEvents({
    click(e) {
      if (readonly) return;
      onPick({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

export default function MapPicker({
  value = null,
  onChange = () => {},
  height = 300,
  center = JUJUY_CENTER,
  zoom = 8,
  readonly = false,
}) {
  const [busqueda, setBusqueda] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [errorBusqueda, setErrorBusqueda] = useState('');
  const lastBusqueda = useRef('');

  const position = value && typeof value.lat === 'number' && typeof value.lng === 'number' ? value : null;
  const mapCenter = position || center;

  async function buscarDireccion(e) {
    e?.preventDefault?.();
    const q = busqueda.trim();
    if (!q || q === lastBusqueda.current) return;
    lastBusqueda.current = q;
    setBuscando(true);
    setErrorBusqueda('');
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=ar&q=${encodeURIComponent(q)}`;
      const res = await fetch(url, { headers: { 'Accept-Language': 'es' } });
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        const { lat, lon } = data[0];
        onChange({ lat: parseFloat(lat), lng: parseFloat(lon) });
      } else {
        setErrorBusqueda('No se encontró la dirección');
      }
    } catch {
      setErrorBusqueda('Error al buscar la dirección');
    } finally {
      setBuscando(false);
    }
  }

  function usarMiUbicacion() {
    if (!navigator.geolocation) {
      setErrorBusqueda('Tu navegador no soporta geolocalización');
      return;
    }
    setBuscando(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onChange({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setBuscando(false);
      },
      () => {
        setErrorBusqueda('No se pudo obtener tu ubicación');
        setBuscando(false);
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }

  return (
    <div style={{ width: '100%' }}>
      {!readonly && (
        <form onSubmit={buscarDireccion} style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar dirección (ej. Av. Belgrano 123, Jujuy)"
            style={{ flex: 1, minWidth: 180, padding: '6px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 14 }}
          />
          <button
            type="submit"
            disabled={buscando || !busqueda.trim()}
            style={{ padding: '6px 14px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, cursor: 'pointer' }}
          >
            {buscando ? 'Buscando…' : 'Buscar'}
          </button>
          <button
            type="button"
            onClick={usarMiUbicacion}
            disabled={buscando}
            title="Usar mi ubicación actual"
            style={{ padding: '6px 10px', background: '#43a047', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, cursor: 'pointer' }}
          >
            <i className="pi pi-map-marker" /> Mi ubicación
          </button>
        </form>
      )}

      {errorBusqueda && (
        <div style={{ color: '#c62828', fontSize: 13, marginBottom: 6 }}>{errorBusqueda}</div>
      )}

      <div style={{ height, width: '100%', border: '1px solid #ddd', borderRadius: 6, overflow: 'hidden' }}>
        <MapContainer
          center={[mapCenter.lat, mapCenter.lng]}
          zoom={position ? 14 : zoom}
          scrollWheelZoom={true}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {position && <Marker position={[position.lat, position.lng]} />}
          {position && <RecenterMap position={position} />}
          <ClickHandler onPick={onChange} readonly={readonly} />
        </MapContainer>
      </div>

      {position && (
        <div style={{ fontSize: 13, color: '#555', marginTop: 6 }}>
          <strong>Coordenadas:</strong> {position.lat.toFixed(6)}, {position.lng.toFixed(6)}
          {!readonly && (
            <button
              type="button"
              onClick={() => onChange(null)}
              style={{ marginLeft: 12, background: 'transparent', border: 'none', color: '#c62828', cursor: 'pointer', fontSize: 13 }}
            >
              <i className="pi pi-times" /> Quitar marker
            </button>
          )}
        </div>
      )}

      {!position && !readonly && (
        <div style={{ fontSize: 13, color: '#888', marginTop: 6 }}>
          Hacé clic en el mapa para seleccionar la ubicación.
        </div>
      )}
    </div>
  );
}
